/*
 * Copyright 2026 Sony Corporation
 */

// Installs registerView. Without it the engine has no multi-canvas support and
// the preview canvas stays black with no error.
import '@babylonjs/core/Engines/AbstractEngine/abstractEngine.views';

import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { DepthOfFieldEffectBlurLevel } from '@babylonjs/core/PostProcesses/depthOfFieldEffect';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ImageProcessingPostProcess } from '@babylonjs/core/PostProcesses/imageProcessingPostProcess';
import { SharpenPostProcess } from '@babylonjs/core/PostProcesses/sharpenPostProcess';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { CreativeLook } from '@ips-device-verse/twin-service/schema';
import type { EngineView } from '@babylonjs/core/Engines/AbstractEngine/abstractEngine.views';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

// Installs Scene.enableDepthRenderer, which the depth-of-field pass reads from.
import '@babylonjs/core/Rendering/depthRendererSceneComponent';
import '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipelineManagerSceneComponent';

import { DEFAULT_OPTICS, fieldOfView, type ResolvedOptics } from './camera-optics';
import { SCENE_LAYER } from './layers';
import { gradeFor } from './creative-look-grade';
import type { SceneEditor } from './scene-editor';

export interface CameraPreview {
  /** Points the preview at a rig, or at nothing. Idempotent. */
  follow: (rig: TransformNode | undefined) => void;
  setLook: (look: Required<CreativeLook>) => void;
  /** Angle of view and focus from the device's lens and sensor. */
  setOptics: (optics: ResolvedOptics) => void;
  /** The blur costs a depth pass and three more, so it is the one thing here that can be turned off. */
  setDepthOfField: (enabled: boolean) => void;
  dispose: () => void;
}

/** Babylon's circle of confusion is in millimetres on a 36 mm-wide frame. */
const FULL_FRAME_WIDTH_MM = 36;

/**
 * Sets a Creative Look on a grading pair. Shared with the stills a shutter
 * release renders, so the picture a PTP client receives is graded the same way
 * as the preview it was framed in.
 */
export const applyLook = (
  grading: ImageProcessingConfiguration,
  sharpen: SharpenPostProcess,
  look: Required<CreativeLook>
): void => {
  const target = gradeFor(look);
  grading.contrast = target.contrast;

  const curves = grading.colorCurves;
  if (curves) {
    curves.globalHue = target.hue;
    curves.globalDensity = target.density;
    curves.globalSaturation = target.saturation;
    curves.highlightsExposure = target.highlights;
    curves.midtonesExposure = target.midtones;
    curves.shadowsExposure = target.shadows;
  }

  sharpen.edgeAmount = target.sharpen;
};

export interface CameraPreviewOptions {
  editor: SceneEditor;
  canvas: HTMLCanvasElement;
}

/**
 * A registered view costs a whole extra render pass, so the preview runs at a
 * third of the editor's rate. It is a framing aid, not an animation.
 */
const RENDER_EVERY_N_FRAMES = 3;

/**
 * What the selected camera sees.
 *
 * A synthetic render of the scene, not a live feed: these cameras have no image
 * sensor here. It is a second registered view on the same engine and the same
 * scene graph, so there is one world and nothing to keep in sync — the preview
 * camera is parented to the rig and follows the gizmo for free.
 *
 * The Creative Look post-processes carry their own `ImageProcessingConfiguration`
 * rather than the scene's, so the main viewport stays neutral and two cameras
 * with different looks remain comparable side by side.
 */
export const createCameraPreview = ({ editor, canvas }: CameraPreviewOptions): CameraPreview => {
  const camera = new TargetCamera('PreviewCamera', Vector3.Zero(), editor.scene);
  camera.fov = fieldOfView(DEFAULT_OPTICS).vertical;
  camera.layerMask = SCENE_LAYER;

  const grading = new ImageProcessingConfiguration();
  grading.colorCurvesEnabled = true;
  grading.colorCurves = new ColorCurves();

  const sharpen = new SharpenPostProcess('PreviewSharpen', 1, camera);
  sharpen.colorAmount = 1;
  sharpen.edgeAmount = 0;

  const grade = new ImageProcessingPostProcess('PreviewGrade', 1, camera);
  grade.imageProcessingConfiguration = grading;
  // Materials already wrote gamma-space colour; encoding it again washes it out.
  grade.fromLinearSpace = false;

  const view: EngineView = editor.engine.registerView(canvas, camera);
  view.enabled = false;

  let followed: TransformNode | undefined;
  let visible = false;
  let frame = 0;
  let optics = DEFAULT_OPTICS;
  let depthOfFieldOn = false;
  // Built on first use: a preview nobody asked to blur pays nothing for it.
  let pipeline: DefaultRenderingPipeline | undefined;

  const applyDepthOfField = () => {
    if (depthOfFieldOn && !pipeline) {
      pipeline = new DefaultRenderingPipeline('PreviewDepthOfField', false, editor.scene, [camera]);
      // The Creative Look grade above is this camera's image processing.
      pipeline.imageProcessingEnabled = false;
      pipeline.depthOfFieldBlurLevel = DepthOfFieldEffectBlurLevel.Medium;
    }
    if (!pipeline) {
      return;
    }
    pipeline.depthOfFieldEnabled = depthOfFieldOn;

    const { focal_length_mm: focal, aperture_f: fStop, focus_distance_m: focus } = optics.optics;
    const effect = pipeline.depthOfField;
    effect.focalLength = focal;
    effect.fStop = fStop;
    effect.focusDistance = focus * 1000;
    // Aperture diameter is lensSize / fStop, so this makes it the real f / N,
    // rescaled so a smaller sensor blurs as much more as its crop says.
    effect.lensSize = focal * (FULL_FRAME_WIDTH_MM / optics.sensor.width);
  };

  const clear = () => canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);

  // Collapsed or scrolled away costs nothing: a second full-rate pass is the
  // easiest way to halve the editor's frame rate on a weak GPU.
  const intersection = new IntersectionObserver((entries) => {
    visible = entries.some((entry) => entry.isIntersecting);
    if (!visible) {
      clear();
    }
  });
  intersection.observe(canvas);

  const observer = editor.engine.onBeginFrameObservable.add(() => {
    frame += 1;
    view.enabled = followed !== undefined && visible && frame % RENDER_EVERY_N_FRAMES === 0;

    if (view.enabled) {
      const { radius } = editor.activeBounds();
      camera.minZ = radius * 0.01;
      camera.maxZ = radius * 200;
    }
  });

  return {
    follow: (rig) => {
      if (rig === followed) {
        return;
      }
      followed = rig;
      camera.parent = rig ?? null;
      camera.position = Vector3.Zero();
      camera.rotation = Vector3.Zero();
      // Static rigs share update counters, so Babylon can mistake the new parent for the old one.
      camera.getViewMatrix(true);
      if (!rig) {
        clear();
      }
    },

    setLook: (look) => applyLook(grading, sharpen, look),

    setOptics: (next) => {
      optics = next;
      camera.fov = fieldOfView(next).vertical;
      applyDepthOfField();
    },

    setDepthOfField: (enabled) => {
      if (enabled === depthOfFieldOn) {
        return;
      }
      depthOfFieldOn = enabled;
      applyDepthOfField();
    },

    dispose: () => {
      intersection.disconnect();
      editor.engine.onBeginFrameObservable.remove(observer);
      editor.engine.unRegisterView(canvas);
      if (pipeline) {
        pipeline.dispose();
        editor.scene.disableDepthRenderer(camera);
      }
      grade.dispose(camera);
      sharpen.dispose(camera);
      camera.dispose();
      clear();
    },
  };
};
